/**
 * Imperative app dialogs (alert / confirm / prompt) with custom modal UI.
 * Mount <AppDialogHost /> once near the app root, then call these helpers.
 */

let _openDialog = null;

export function registerAppDialogHost(openFn) {
  _openDialog = typeof openFn === "function" ? openFn : null;
  return () => {
    if (_openDialog === openFn) _openDialog = null;
  };
}

function openDialog(config) {
  if (!_openDialog) {
    console.error("AppDialogHost is not mounted; falling back to native dialogs.");
    return fallbackNative(config);
  }
  return _openDialog(config);
}

function fallbackNative(config) {
  const message = String(config?.message || "");
  if (config?.kind === "confirm") {
    return Promise.resolve(window.confirm(message));
  }
  if (config?.kind === "prompt") {
    const value = window.prompt(message, config.defaultValue ?? "");
    return Promise.resolve(value);
  }
  window.alert(message);
  return Promise.resolve(undefined);
}

/** Simple notice. Resolves when the user dismisses. */
export function alertDialog({
  title = "Notice",
  message,
  confirmLabel = "OK",
} = {}) {
  return openDialog({
    kind: "alert",
    title,
    message: String(message || ""),
    confirmLabel,
  });
}

/** Ask yes/no. Resolves true if confirmed, false if cancelled. */
export function confirmDialog({
  title = "Please confirm",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
} = {}) {
  return openDialog({
    kind: "confirm",
    title,
    message: String(message || ""),
    confirmLabel,
    cancelLabel,
    tone: tone === "danger" ? "danger" : "default",
  });
}

/**
 * Ask for text input. Resolves trimmed string, or null if cancelled.
 * Pass `options` for a select list instead of a free-text field.
 */
export function promptDialog({
  title = "Enter a value",
  message = "",
  label = "",
  defaultValue = "",
  placeholder = "",
  inputType = "text",
  options = null,
  confirmLabel = "Continue",
  cancelLabel = "Cancel",
  required = false,
  minLength = 0,
  tone = "default",
} = {}) {
  return openDialog({
    kind: "prompt",
    title,
    message: String(message || ""),
    label: String(label || ""),
    defaultValue: defaultValue == null ? "" : String(defaultValue),
    placeholder: String(placeholder || ""),
    inputType: ["text", "textarea", "number", "email", "url"].includes(inputType)
      ? inputType
      : "text",
    options: Array.isArray(options) && options.length ? options.map(String) : null,
    confirmLabel,
    cancelLabel,
    required: Boolean(required),
    minLength: Math.max(0, Number(minLength) || 0),
    tone: tone === "danger" ? "danger" : "default",
  });
}
